// Centralized, crash-fast environment configuration for the web app.
//
// IMPORTANT:
// - Do NOT provide hardcoded fallbacks for required variables.
// - Missing variables should throw immediately at startup.
//
// NOTE: Emulator connection is automatically determined in firebase.ts based on
// VITE_ENVIRONMENT. When set to 'local', emulators are used. All other values
// (dev, staging, prod) connect to real Firebase projects.

export type FirebaseWebConfig = {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
};

const requireEnv = (key: string): string => {
  const raw = (import.meta.env as Record<string, unknown>)[key];
  if (typeof raw !== "string" || raw.trim().length === 0) {
    throw new Error(`[env] Missing required environment variable: ${key}`);
  }
  return raw;
};

const normalizeBaseUrl = (url: string): string => url.replace(/\/+$/, "");

const deriveDocsUrl = (apiBaseUrl: string): string => {
  // Common case: http://host:8000/api/v1 => http://host:8000/docs
  if (apiBaseUrl.endsWith("/api/v1")) {
    return `${apiBaseUrl.slice(0, -"/api/v1".length)}/docs`;
  }
  return `${apiBaseUrl}/docs`;
};

export const env = {
  isDev: import.meta.env.DEV,

  // Environment: local, dev, staging, or prod
  environment: (import.meta.env.VITE_ENVIRONMENT as string) || "production",

  // Backend base URL (e.g. http://localhost:8000/api/v1)
  apiBaseUrl: normalizeBaseUrl(requireEnv("VITE_API_BASE_URL")),
  apiDocsUrl: "", // filled below

  mapboxToken: requireEnv("VITE_MAPBOX_TOKEN"),

  firebase: {
    apiKey: requireEnv("VITE_FIREBASE_API_KEY"),
    authDomain: requireEnv("VITE_FIREBASE_AUTH_DOMAIN"),
    projectId: requireEnv("VITE_FIREBASE_PROJECT_ID"),
    storageBucket: requireEnv("VITE_FIREBASE_STORAGE_BUCKET"),
    messagingSenderId: requireEnv("VITE_FIREBASE_MESSAGING_SENDER_ID"),
    appId: requireEnv("VITE_FIREBASE_APP_ID"),
  },
};

env.apiDocsUrl = deriveDocsUrl(env.apiBaseUrl);
